import { camelize, pluralize } from "inflected";

type Node = { apiIdentifier: string };
type NodeWithOptionalNamespace = Node & { namespace?: string[] | null };

export interface APIElementNamer {
  pluralApiIdentifier(): string;
  fullyQualifiedApiIdentifier(): string;
  camelizedFullyQualifiedApiIdentifier(): string;
  fullyQualifiedPluralApiIdentifier(): string;
  recordTypeName(): string;
  actionApiIdentifier(action: NodeWithOptionalNamespace): string;
  internalActionTriggerIdentifier(action: NodeWithOptionalNamespace): string;
  apiNamespaceForAction(action: NodeWithOptionalNamespace): string[];
  imperativeModelNamespace(action: Node): string;
  actionMainArgumentName(action: NodeWithOptionalNamespace): string;
  actionInputTypeName(action: NodeWithOptionalNamespace, input: { namespace?: string[] | null; apiIdentifier: string }): string;
  actionOutputTypeName(action: NodeWithOptionalNamespace): string;
  actionOutputBaseTypeName(action: NodeWithOptionalNamespace): string;
  bulkActionApiIdentifier(action: Node): string;
  bulkActionInputTypeName(action: Node): string;
  bulkActionOutputTypeName(action: Node): string;
  acceptActionRecordIdentities(): boolean;
  sortTypeName(): string;
  filterTypeName(): string;
  relationshipFilterTypeName(isHasMany: boolean, infix?: string): string;
  internalRecordTypeName(): string;
  internalModelInputTypeName(): string;
  internalMutationResultTypeName(mutationType: string): string;
  internalAtomicsInputTypeName(): string;
  internalDeleteManyResultTypeName(): string;
  internalBulkCreateResultTypeName(): string;
}

/**
 * Check if two API Identifiers match by checking if they are the same in camelCase and lowercase
 */
export const detectGraphQLTypenameCollision = (source: string, target: string) => {
  const result = camelize(source).toLowerCase() === camelize(target).toLowerCase();
  return result;
};

/**
 * Generate a string that combines the namespaces and the API Identifier in a camelCase format.
 *
 * Example:
 * ```ts
 * combineNamespacesAndApiIdentifier(["foo", "bar"], "baz");
 * // Returns "fooBarBaz"
 * combineNamespacesAndApiIdentifier(["foo"], "baz");
 * // Returns "fooBaz"
 * combineNamespacesAndApiIdentifier([], "baz");
 * // Returns "baz"
 * ```
 */
export const combineNamespacesAndApiIdentifier = (namespaces: string[], apiIdentifier: string) => {
  const combinedNamespaces = namespaces.map((segment, index) => (index === 0 ? segment : camelize(segment))).join("");
  return namespaces.length > 0 ? `${combinedNamespaces}${camelize(apiIdentifier)}` : apiIdentifier;
};

export const pluralizeApiIdentifier = (apiIdentifier: string) => {
  const pluralized = pluralize(apiIdentifier);
  if (pluralized == apiIdentifier) {
    return apiIdentifier + "s";
  } else {
    return pluralized;
  }
};

/** Used to name all the stuff for a model's API for almost every model */
export class StandardAPIElementNamer implements APIElementNamer {
  constructor(readonly model: { apiIdentifier: string; name?: string; namespace: string[] }) {}

  internalActionTriggerIdentifier(action: { apiIdentifier: string; namespace: string[] }): string {
    return `trigger${camelize(this.actionApiIdentifier(action), true)}`;
  }

  fullyQualifiedApiIdentifier() {
    return combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier);
  }

  camelizedFullyQualifiedApiIdentifier() {
    return camelize(this.fullyQualifiedApiIdentifier());
  }

  pluralApiIdentifier() {
    return pluralizeApiIdentifier(this.model.apiIdentifier);
  }

  fullyQualifiedPluralApiIdentifier() {
    return combineNamespacesAndApiIdentifier(this.model.namespace, this.pluralApiIdentifier());
  }

  recordTypeName() {
    return this.camelizedFullyQualifiedApiIdentifier();
  }

  actionApiIdentifier(action: NodeWithOptionalNamespace) {
    return `${combineNamespacesAndApiIdentifier(action.namespace ?? [], action.apiIdentifier)}${camelize(this.model.apiIdentifier)}`;
  }

  apiNamespaceForAction(_action: Node): string[] {
    return this.model.namespace;
  }

  actionInputTypeName(action: NodeWithOptionalNamespace, input: NodeWithOptionalNamespace) {
    const actionName = combineNamespacesAndApiIdentifier(action.namespace ?? [], action.apiIdentifier);
    const inputName = input.namespace ? combineNamespacesAndApiIdentifier(input.namespace, input.apiIdentifier) : input.apiIdentifier;
    return `${camelize(actionName)}${camelize(inputName)}Input`;
  }

  actionOutputBaseTypeName(action: NodeWithOptionalNamespace) {
    return `${camelize(combineNamespacesAndApiIdentifier(action.namespace ?? [], action.apiIdentifier))}${camelize(
      combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier)
    )}`;
  }

  actionOutputTypeName(action: NodeWithOptionalNamespace) {
    return `${this.actionOutputBaseTypeName(action)}Result`;
  }

  actionMainArgumentName(_action: NodeWithOptionalNamespace) {
    return this.model.apiIdentifier;
  }

  bulkActionApiIdentifier(action: Node) {
    return `bulk${camelize(action.apiIdentifier)}${camelize(pluralize(this.model.apiIdentifier))}`;
  }

  bulkActionInputTypeName(action: Node) {
    return `Bulk${camelize(action.apiIdentifier)}${camelize(
      pluralize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))
    )}Input`;
  }

  bulkActionOutputTypeName(action: Node) {
    return `Bulk${camelize(action.apiIdentifier)}${camelize(
      pluralize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))
    )}Result`;
  }

  sortTypeName() {
    return `${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))}Sort`;
  }

  filterTypeName() {
    return `${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))}Filter`;
  }

  relationshipFilterTypeName(isHasMany: boolean, infix = "") {
    return `${camelize(
      combineNamespacesAndApiIdentifier(this.model.namespace, isHasMany ? this.pluralApiIdentifier() : this.model.apiIdentifier)
    )}${infix}RelationshipFilter`;
  }

  acceptActionRecordIdentities() {
    return true;
  }

  imperativeModelNamespace(action: Node) {
    return [...this.apiNamespaceForAction(action), this.model.apiIdentifier].join(".");
  }

  internalRecordTypeName(): string {
    return `Internal${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))}Record`;
  }
  internalModelInputTypeName(): string {
    return `Internal${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))}Input`;
  }
  internalMutationResultTypeName(mutationType: string): string {
    return `Internal${camelize(mutationType)}${camelize(
      combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier)
    )}Result`;
  }
  internalAtomicsInputTypeName(): string {
    return `Internal${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))}AtomicsInput`;
  }
  internalDeleteManyResultTypeName(): string {
    return `InternalDeleteMany${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.model.apiIdentifier))}Result`;
  }
  internalBulkCreateResultTypeName(): string {
    return `InternalBulkCreate${camelize(combineNamespacesAndApiIdentifier(this.model.namespace, this.pluralApiIdentifier()))}Result`;
  }
}

/** Used to name all the stuff for a the Session model which is presented differently in the GraphQL API */
export class SingletonAPIElementNamer extends StandardAPIElementNamer implements APIElementNamer {
  actionApiIdentifier(action: NodeWithOptionalNamespace) {
    return `${action.apiIdentifier}`;
  }

  currentSingletonApiIdentifier() {
    return `current${camelize(this.model.apiIdentifier)}`;
  }

  apiNamespaceForAction(_action: NodeWithOptionalNamespace) {
    return [this.currentSingletonApiIdentifier()];
  }

  acceptActionRecordIdentities() {
    return false;
  }

  imperativeModelNamespace(_action: Node) {
    return this.currentSingletonApiIdentifier();
  }
}

/** Used to name global actions in the API */
export class GlobalActionAPINamer implements APIElementNamer {
  internalActionTriggerIdentifier(action: NodeWithOptionalNamespace): string {
    return `trigger${camelize(this.actionApiIdentifier(action), true)}`;
  }

  actionApiIdentifier(action: NodeWithOptionalNamespace) {
    return action.apiIdentifier;
  }

  apiNamespaceForAction(action: NodeWithOptionalNamespace) {
    return action.namespace ?? [];
  }

  actionInputTypeName(action: NodeWithOptionalNamespace, field: NodeWithOptionalNamespace) {
    const actionName = combineNamespacesAndApiIdentifier(action.namespace ?? [], action.apiIdentifier);
    const inputName = field.namespace ? combineNamespacesAndApiIdentifier(field.namespace, field.apiIdentifier) : field.apiIdentifier;
    return `${camelize(actionName)}${camelize(inputName)}Input`;
  }

  actionOutputBaseTypeName(action: NodeWithOptionalNamespace) {
    return `${camelizedNamespacedApiId(action.namespace ?? [], action.apiIdentifier)}`;
  }

  actionOutputTypeName(action: NodeWithOptionalNamespace) {
    return `${this.actionOutputBaseTypeName(action)}Result`;
  }

  actionMainArgumentName(_action: NodeWithOptionalNamespace) {
    return "params";
  }

  acceptActionRecordIdentities(): boolean {
    return false;
  }
  pluralApiIdentifier(): string {
    throw new Error("Method not implemented.");
  }
  fullyQualifiedApiIdentifier(): string {
    throw new Error("Method not implemented.");
  }
  camelizedFullyQualifiedApiIdentifier(): string {
    throw new Error("Method not implemented.");
  }
  fullyQualifiedPluralApiIdentifier(): string {
    throw new Error("Method not implemented.");
  }
  recordTypeName(): string {
    throw new Error("Method not implemented.");
  }
  bulkActionApiIdentifier(_action: Node): string {
    throw new Error("Method not implemented.");
  }
  bulkActionInputTypeName(_action: Node): string {
    throw new Error("Method not implemented.");
  }
  bulkActionOutputTypeName(_action: Node): string {
    throw new Error("Method not implemented.");
  }
  sortTypeName(): string {
    throw new Error("Method not implemented.");
  }
  filterTypeName(): string {
    throw new Error("Method not implemented.");
  }
  relationshipFilterTypeName(_isHasMany: boolean, _infix: string): string {
    throw new Error("Method not implemented.");
  }
  imperativeModelNamespace(_action: Node): string {
    throw new Error("Method not implemented.");
  }
  internalRecordTypeName(): string {
    throw new Error("Method not implemented.");
  }
  internalModelInputTypeName(): string {
    throw new Error("Method not implemented.");
  }
  internalMutationResultTypeName(_mutationType: string): string {
    throw new Error("Method not implemented.");
  }
  internalAtomicsInputTypeName(): string {
    throw new Error("Method not implemented.");
  }
  internalDeleteManyResultTypeName(): string {
    throw new Error("Method not implemented.");
  }
  internalBulkCreateResultTypeName(): string {
    throw new Error("Method not implemented.");
  }
}

export const relationIdFieldName = (field: Node) => field.apiIdentifier + "Id";
export const camelizedNamespacedApiId = (namespace: string[], apiIdentifier: string) =>
  camelize(combineNamespacesAndApiIdentifier(namespace, apiIdentifier));

export const relationIdFieldNameToApiIdentifier = (fieldName: string) => fieldName.replace(/Id$/, "");
